import { forwardRef, type ElementType, type HTMLAttributes } from "react";
import { cn } from "@/lib/cn";
import { TEXT_SIZE_CLASS, type Scale } from "./tokens";

const FONT_WEIGHT_CLASS = {
  400: "font-normal",
  500: "font-medium",
  600: "font-semibold",
  700: "font-bold",
} as const;

export interface TextProps extends HTMLAttributes<HTMLElement> {
  size?: Scale;
  /**
   * Color de texto · `"dimmed"` (el más usado, 264 veces en el repo Mantine)
   * mapea al token semántico `muted-foreground`; cualquier otro string se
   * resuelve como `var(--color-{c})` (ej. `c="peligro-600"`). Va por
   * `style` (no por clase Tailwind) A PROPÓSITO: `c` suele venir de una prop
   * dinámica en runtime (ej. el color de un estado), y Tailwind solo genera
   * clases para strings que puede ESCANEAR en el código fuente · un
   * `text-${c}` armado en runtime nunca aparecería en el CSS compilado. Las
   * variables `--color-*` sí están siempre disponibles (las emite `@theme`
   * incondicionalmente), así que `var()` resuelve sin ese problema.
   */
  c?: "dimmed" | (string & {});
  fw?: keyof typeof FONT_WEIGHT_CLASS;
  /** Fuente monoespaciada · para RFCs, UUIDs, montos, folios. */
  ff?: "mono";
  tt?: "uppercase" | "lowercase" | "capitalize";
  ta?: "left" | "center" | "right";
  /** Corta con "…" en una sola línea (requiere que el padre limite el ancho). */
  truncate?: boolean;
  /** Renderiza `<span>` (inline) en vez de `<p>` (default, igual que Mantine). */
  span?: boolean;
}

/**
 * Reemplazo de `<Text>` de Mantine · mismas props (`size`/`c`/`fw`/`ff`/`tt`/
 * `ta`) para que los ~1500 usos existentes migren cambiando solo el import.
 */
export const Text = forwardRef<HTMLElement, TextProps>(
  (
    { size = "md", c, fw, ff, tt, ta, truncate, span, className, style, ...props },
    ref
  ) => {
    const Component = (span ? "span" : "p") as ElementType;
    return (
      <Component
        ref={ref}
        className={cn(
          TEXT_SIZE_CLASS[size],
          c === "dimmed" && "text-muted-foreground",
          fw && FONT_WEIGHT_CLASS[fw],
          ff === "mono" && "font-mono",
          tt,
          ta === "left" ? "text-left" : ta === "center" ? "text-center" : ta === "right" ? "text-right" : undefined,
          truncate && "overflow-hidden text-ellipsis whitespace-nowrap",
          className
        )}
        style={
          c && c !== "dimmed" ? { color: `var(--color-${c})`, ...style } : style
        }
        {...props}
      />
    );
  }
);
Text.displayName = "Text";
